import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  FileText, 
  Receipt, 
  Plus,
  Calendar as CalendarIcon,
  DollarSign,
  Loader2,
  ChevronRight,
  ChevronDown,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Send,
  X,
  Edit2,
  Music,
  Bell,
  ExternalLink,
  Download,
  Mail,
  Eye,
  FolderOpen,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import PricingModal from '../components/PricingModal';
import WalkthroughDemo from '../components/WalkthroughDemo';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const EVENT_TYPES = [
  { value: 'booking', label: 'Booking', color: '#FF4500', icon: Music },
  { value: 'event', label: 'Event', color: '#10B981', icon: CalendarIcon },
  { value: 'invoice', label: 'Invoice Due', color: '#F59E0B', icon: Receipt },
  { value: 'reminder', label: 'Reminder', color: '#8B5CF6', icon: Bell },
];

export default function DashboardPage() {
  const { user, planInfo, fetchPlanInfo } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [letters, setLetters] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [adminPlanLoading, setAdminPlanLoading] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [openMonths, setOpenMonths] = useState({});
  const [eventForm, setEventForm] = useState({
    title: '',
    event_type: 'booking',
    date: '',
    time: '',
    client_name: '',
    amount: '',
    currency: 'GBP',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Show walkthrough for first-time users or on replay
  useEffect(() => {
    if (user) {
      const replay = localStorage.getItem('settledinfull_replay_walkthrough');
      if (replay) {
        localStorage.removeItem('settledinfull_replay_walkthrough');
        setShowWalkthrough(true);
        return;
      }
      const seen = localStorage.getItem('settledinfull_walkthrough_seen');
      if (!seen) {
        setShowWalkthrough(true);
      }
    }
  }, [user]);

  // Listen for replay trigger from navbar (when already on dashboard)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'settledinfull_replay_walkthrough' && e.newValue === 'true') {
        localStorage.removeItem('settledinfull_replay_walkthrough');
        setShowWalkthrough(true);
      }
    };
    // Also poll for same-tab writes (storage event only fires cross-tab)
    const interval = setInterval(() => {
      const replay = localStorage.getItem('settledinfull_replay_walkthrough');
      if (replay) {
        localStorage.removeItem('settledinfull_replay_walkthrough');
        setShowWalkthrough(true);
      }
    }, 300);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    // Fetch events when month changes
    const month = selectedDate.getMonth() + 1;
    const year = selectedDate.getFullYear();
    fetchEvents(month, year);
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const [invoicesRes, lettersRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/letters/history`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/recovery`, { withCredentials: true })
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setLetters(lettersRes.data.letters || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const fetchEvents = async (month, year) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/events?month=${month}&year=${year}`,
        { withCredentials: true }
      );
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/invoices/${id}`, { withCredentials: true });
      setInvoices(prev => prev.filter(inv => inv.id !== id));
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleDuplicateInvoice = (invoice) => {
    const today = toLocalDateStr(new Date());
    const formData = {
      business_name: invoice.business_name || '',
      business_address: invoice.business_address || '',
      business_email: invoice.business_email || '',
      business_phone: invoice.business_phone || '',
      logo_path: invoice.logo_path || '',
      logo_position: invoice.logo_position || 'left',
      font_family: invoice.font_family || 'Inter',
      header_style: invoice.header_style || 'classic',
      include_banking: invoice.include_banking || false,
      bank_account_number: invoice.bank_account_number || '',
      bank_sort_code: invoice.bank_sort_code || '',
      bank_iban: invoice.bank_iban || '',
      bank_swift: invoice.bank_swift || '',
      client_name: '',
      client_address: '',
      client_email: '',
      invoice_number: '',
      invoice_date: today,
      due_date: '',
      event_date: invoice.event_date || '',
      items: invoice.items?.length > 0
        ? invoice.items.map(item => ({ ...item }))
        : [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      subtotal: invoice.subtotal || 0,
      deposit: 0,
      balance_due: invoice.subtotal || 0,
      currency: invoice.currency || 'GBP',
      payment_method: invoice.payment_method || 'Bank Transfer',
      terms: invoice.terms || '',
      notes: invoice.notes || '',
    };
    localStorage.setItem('sif_invoice_form', JSON.stringify({
      form: formData,
      template: invoice.template_id || 1,
    }));
    toast.success('Invoice duplicated — edit the client details');
    navigate('/invoice-creator');
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/invoices/${invoiceId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      setInvoices(prev => prev.map(inv => 
        inv.id === invoiceId ? { ...inv, status: newStatus } : inv
      ));
      
      toast.success(`Invoice marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Invoice PDF download
  const handleDownloadInvoicePDF = async (invoice) => {
    const effective = planInfo?.limits?.can_download;
    if (!effective) {
      setShowPricing(true);
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/invoices/${invoice.id}/pdf`,
        {},
        { responseType: 'blob', withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
      fetchPlanInfo();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('upgrade_required')) {
        setShowPricing(true);
      } else {
        toast.error('Failed to download invoice PDF');
      }
    }
  };

  const handleSendInvoice = async (invoice) => {
    const effective = planInfo?.limits?.can_download;
    if (!effective) {
      setShowPricing(true);
      return;
    }

    // Build and open mailto IMMEDIATELY (within user gesture context, before any async calls)
    const to = invoice.client_email || '';
    const invNum = invoice.invoice_number || invoice.id;
    const bizName = invoice.business_name || 'our team';
    const amount = formatCurrency(invoice.balance_due, invoice.currency);
    const subject = encodeURIComponent(`Invoice #${invNum} from ${bizName}`);
    const body = encodeURIComponent(
      `Hi ${invoice.client_name || 'there'},\n\n` +
      `Please find attached invoice #${invNum} for ${amount}.\n\n` +
      `Due date: ${invoice.due_date || 'See invoice'}\n\n` +
      `If you have any questions, please don't hesitate to get in touch.\n\n` +
      `Kind regards,\n${bizName}`
    );
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_self');

    // Then download the PDF asynchronously
    try {
      const response = await axios.post(
        `${API_URL}/api/invoices/${invoice.id}/pdf`,
        {},
        { responseType: 'blob', withCredentials: true }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      fetchPlanInfo();
      toast.success('PDF downloaded — attach it to your email', { duration: 5000 });
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('upgrade_required')) {
        setShowPricing(true);
      } else {
        toast.error('Failed to download invoice PDF');
      }
    }
  };

  // Invoice preview dialog
  const [previewInvoice, setPreviewInvoice] = useState(null);

  // Letter view dialog
  const [viewingLetter, setViewingLetter] = useState(null);

  // Letter PDF download
  const handleDownloadLetterPDF = async (letter, tone) => {
    const effective = planInfo?.limits?.can_download;
    if (!effective) {
      setShowPricing(true);
      return;
    }
    try {
      const content = letter.letters?.[tone] || letter.letters?.friendly || '';
      const toneLabel = tone.charAt(0).toUpperCase() + tone.slice(1);
      const response = await axios.post(
        `${API_URL}/api/letters/pdf`,
        {
          content,
          letter_type: `${toneLabel} Payment Email`,
          business_name: letter.request_data?.business_name || '',
        },
        { responseType: 'blob', withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `email_${tone}_${letter.request_data?.client_name || 'client'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Email PDF downloaded');
    } catch (error) {
      toast.error('Failed to download email PDF');
    }
  };

  // Letter delete
  const handleDeleteLetter = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email?')) return;
    try {
      await axios.delete(`${API_URL}/api/letters/${id}`, { withCredentials: true });
      setLetters(prev => prev.filter(l => l.id !== id));
      toast.success('Email deleted');
    } catch (error) {
      toast.error('Failed to delete email');
    }
  };

  const handleOpenEventDialog = (date = null, event = null) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        title: event.title,
        event_type: event.event_type,
        date: event.date,
        time: event.time || '',
        client_name: event.client_name || '',
        amount: event.amount?.toString() || '',
        currency: event.currency || 'GBP',
        notes: event.notes || ''
      });
    } else {
      setEditingEvent(null);
      const dateStr = date ? toLocalDateStr(date) : toLocalDateStr(new Date());
      setEventForm({
        title: '',
        event_type: 'booking',
        date: dateStr,
        time: '',
        client_name: '',
        amount: '',
        currency: 'GBP',
        notes: ''
      });
    }
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.date) {
      toast.error('Please fill in title and date');
      return;
    }

    try {
      const payload = {
        ...eventForm,
        amount: eventForm.amount ? parseFloat(eventForm.amount) : null
      };

      if (editingEvent) {
        await axios.put(
          `${API_URL}/api/events/${editingEvent.id}`,
          payload,
          { withCredentials: true }
        );
        // Update local state immediately
        setEvents(prev => prev.map(e => 
          e.id === editingEvent.id 
            ? { ...e, ...payload, color: EVENT_TYPES.find(t => t.value === payload.event_type)?.color || '#FF4500' }
            : e
        ));
        toast.success('Event updated');
      } else {
        const response = await axios.post(
          `${API_URL}/api/events`,
          payload,
          { withCredentials: true }
        );
        // Add new event to local state immediately
        const newEvent = response.data.event;
        setEvents(prev => [...prev, newEvent]);
        toast.success('Event created');
      }

      setShowEventDialog(false);
      // Also fetch to ensure sync with server
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();
      fetchEvents(month, year);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Failed to save event';
      console.error('Event save error:', error.response?.status, detail);
      toast.error(detail);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;

    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`, { withCredentials: true });
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleExportToGoogleCalendar = async (eventId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/events/${eventId}/google-calendar`,
        { withCredentials: true }
      );
      window.open(response.data.google_calendar_url, '_blank');
      toast.success('Opening Google Calendar');
    } catch (error) {
      toast.error('Failed to export to Google Calendar');
    }
  };

  const handleExportAllEvents = async () => {
    try {
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();
      const response = await axios.get(
        `${API_URL}/api/events/export/ical?month=${month}&year=${year}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'settledinfull_events.ics');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success('Calendar exported!');
    } catch (error) {
      console.error('Export error:', error.response?.status, error.message);
      toast.error('Failed to export calendar');
    }
  };

  const handleSendReminder = async (eventId) => {
    try {
      await axios.post(
        `${API_URL}/api/events/${eventId}/send-reminder`,
        {},
        { withCredentials: true }
      );
      toast.success('Reminder email sent!');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  // Helper to get local date string (YYYY-MM-DD) without UTC shift
  const toLocalDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Parse as local date to avoid timezone shift
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'sent':
        return <Send className="w-4 h-4 text-blue-500" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-500/20 text-green-400';
      case 'sent':
        return 'bg-blue-500/20 text-blue-400';
      case 'overdue':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  // Group invoices by month/year
  const groupedInvoices = React.useMemo(() => {
    const groups = {};
    const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    invoices.forEach((inv) => {
      const dateStr = inv.invoice_date || inv.created_at || '';
      const parts = dateStr.split('T')[0].split('-');
      const year = parts[0] || 'Unknown';
      const monthIdx = parseInt(parts[1], 10) - 1;
      const monthName = MONTH_NAMES[monthIdx] || 'Unknown';
      const key = `${monthName} ${year}`;
      if (!groups[key]) groups[key] = { key, label: key, sortKey: `${year}-${parts[1] || '00'}`, invoices: [] };
      groups[key].invoices.push(inv);
    });
    return Object.values(groups).sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }, [invoices]);

  // Auto-open current month on first load
  useEffect(() => {
    if (groupedInvoices.length > 0 && Object.keys(openMonths).length === 0) {
      setOpenMonths({ [groupedInvoices[0].key]: true });
    }
  }, [groupedInvoices]);

  const toggleMonth = (key) => {
    setOpenMonths(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return events.filter(e => e.date === dateStr);
  };

  // Custom day render for calendar
  const getDayContent = (day) => {
    const dayEvents = getEventsForDate(day);
    if (dayEvents.length === 0) return null;
    
    return (
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-0.5 pb-0.5">
        {dayEvents.slice(0, 3).map((event, idx) => (
          <div 
            key={idx}
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: event.color || '#FF4500' }}
          />
        ))}
      </div>
    );
  };

  // Get selected date events
  const selectedDateEvents = getEventsForDate(selectedDate);

  const handleAdminSetPlan = async (newPlan) => {
    setAdminPlanLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/set-plan`, { plan: newPlan }, { withCredentials: true });
      toast.success(`Plan set to ${newPlan}`);
      fetchPlanInfo();
    } catch (error) {
      toast.error('Failed to change plan');
    }
    setAdminPlanLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050507] pt-20 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#FF4500] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] pt-20" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-clash text-4xl font-medium text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-white/60">Manage your invoices, events and track your payments</p>
        </div>

        {/* Credit Balance Banner */}
        {planInfo && (
          <div className="mb-6 bg-surface p-4 rounded-md border border-white/8 flex flex-wrap items-center justify-between gap-4" data-testid="plan-status-banner">
            <div className="flex items-center gap-3 flex-wrap">
              <div className={`w-2 h-2 rounded-full ${planInfo.credits > 0 ? 'bg-green-500' : 'bg-white/30'}`} />
              <span className="text-white/70 text-sm">
                Credits: <span className="text-white font-medium">{planInfo.credits}</span>
              </span>
              {planInfo.credits_expire_at && planInfo.credits > 0 && (
                <span className="text-amber-400 text-xs font-medium">
                  Expires: {new Date(planInfo.credits_expire_at).toLocaleDateString()} ({Math.max(0, Math.ceil((new Date(planInfo.credits_expire_at) - new Date()) / 86400000))} days left)
                </span>
              )}
              {planInfo.credits === 0 && (
                <span className="text-white/40 text-xs">No credits remaining</span>
              )}
            </div>
            <Button
              onClick={() => setShowPricing(true)}
              size="sm"
              className="bg-[#FF4500] hover:bg-[#e03e00] text-white text-xs"
              data-testid="get-credits-btn"
            >
              {planInfo.credits > 0 ? 'Get More Credits' : 'Get Credits'}
            </Button>
          </div>
        )}

        {/* Admin Credit Toggle */}
        {user?.role === 'admin' && (
          <div className="mb-6 bg-[#1a1a2e] p-4 rounded-md border border-purple-500/20" data-testid="admin-plan-toggle">
            <p className="text-purple-300 text-xs font-semibold uppercase tracking-wider mb-2">Admin: Test Credit Levels</p>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'zero', label: 'No Credits', credits: 0 },
                { id: 'free', label: 'Free (1)', credits: 1 },
                { id: 'instant', label: 'Instant (3)', credits: 3 },
                { id: 'get_paid', label: 'Get Paid (20)', credits: 20 },
                { id: 'pro', label: 'Pro (45)', credits: 45 },
              ].map((p) => (
                <Button
                  key={p.id}
                  size="sm"
                  disabled={adminPlanLoading}
                  onClick={() => handleAdminSetPlan(p.id)}
                  data-testid={`admin-set-plan-${p.id}`}
                  className="text-xs bg-white/5 text-white/60 hover:bg-white/10"
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Total Recovered</span>
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
              {analytics.by_currency && Object.keys(analytics.by_currency).filter(c => analytics.by_currency[c].recovered > 0).length > 0 ? (
                <div className="space-y-1">
                  {Object.entries(analytics.by_currency)
                    .filter(([, v]) => v.recovered > 0)
                    .sort(([, a], [, b]) => b.recovered - a.recovered)
                    .map(([currency, data]) => (
                      <p key={currency} className="text-2xl font-bold text-green-400">
                        {formatCurrency(data.recovered, currency)}
                      </p>
                    ))}
                </div>
              ) : (
                <p className="text-3xl font-bold text-green-400">
                  {formatCurrency(0)}
                </p>
              )}
              <p className="text-xs text-white/40 mt-1">
                {analytics.paid_count} paid invoice{analytics.paid_count !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Pending Amount</span>
                <Clock className="w-4 h-4 text-yellow-500" />
              </div>
              {analytics.by_currency && Object.keys(analytics.by_currency).filter(c => analytics.by_currency[c].pending > 0).length > 0 ? (
                <div className="space-y-1">
                  {Object.entries(analytics.by_currency)
                    .filter(([, v]) => v.pending > 0)
                    .sort(([, a], [, b]) => b.pending - a.pending)
                    .map(([currency, data]) => (
                      <p key={currency} className="text-2xl font-bold text-yellow-400">
                        {formatCurrency(data.pending, currency)}
                      </p>
                    ))}
                </div>
              ) : (
                <p className="text-3xl font-bold text-yellow-400">
                  {formatCurrency(0)}
                </p>
              )}
              <p className="text-xs text-white/40 mt-1">
                {analytics.pending_count} pending invoice{analytics.pending_count !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Recovery Rate</span>
                <CheckCircle className="w-4 h-4 text-[#FF4500]" />
              </div>
              <p className="text-3xl font-bold text-[#FF4500]">
                {analytics.recovery_rate}%
              </p>
              <p className="text-xs text-white/40 mt-1">
                of invoices paid
              </p>
            </div>

            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Total Invoiced</span>
                <DollarSign className="w-4 h-4 text-white/30" />
              </div>
              {analytics.by_currency && Object.keys(analytics.by_currency).length > 0 ? (
                <div className="space-y-1">
                  {Object.entries(analytics.by_currency)
                    .filter(([, v]) => v.total > 0)
                    .sort(([, a], [, b]) => b.total - a.total)
                    .map(([currency, data]) => (
                      <p key={currency} className="text-2xl font-bold text-white">
                        {formatCurrency(data.total, currency)}
                      </p>
                    ))}
                </div>
              ) : (
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(0)}
                </p>
              )}
              <p className="text-xs text-white/40 mt-1">
                {analytics.total_invoices} total invoice{analytics.total_invoices !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Link to="/email-generator">
            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow hover-lift cursor-pointer group">
              <div className="w-12 h-12 rounded-md bg-[#FF4500]/10 flex items-center justify-center mb-4 group-hover:bg-[#FF4500]/20 transition-colors">
                <FileText className="w-6 h-6 text-[#FF4500]" />
              </div>
              <h3 className="font-medium text-white mb-1">Generate Emails</h3>
              <p className="text-white/50 text-sm">Create collection emails</p>
            </div>
          </Link>
          
          <Link to="/invoice-creator">
            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow hover-lift cursor-pointer group">
              <div className="w-12 h-12 rounded-md bg-[#10B981]/10 flex items-center justify-center mb-4 group-hover:bg-[#10B981]/20 transition-colors">
                <Receipt className="w-6 h-6 text-[#10B981]" />
              </div>
              <h3 className="font-medium text-white mb-1">Create Invoice</h3>
              <p className="text-white/50 text-sm">New professional invoice</p>
            </div>
          </Link>
        </div>

        {/* Invoices & Emails Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Invoices */}
          <div className="bg-surface p-6 rounded-md border border-white/8 card-glow">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-clash text-xl font-medium text-white">Your Invoices</h2>
              <Link to="/invoice-creator">
                <Button size="sm" className="bg-[#FF4500] hover:bg-[#CC3700]">
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Button>
              </Link>
            </div>

            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50 mb-4">No invoices yet</p>
                <Link to="/invoice-creator">
                  <Button variant="outline" className="border-white/10 text-white hover:bg-white/5">
                    Create your first invoice
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2" data-testid="invoice-month-groups">
                {groupedInvoices.map((group) => (
                  <div key={group.key} className="rounded-md border border-white/8 overflow-hidden">
                    <button
                      onClick={() => toggleMonth(group.key)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                      data-testid={`month-folder-${group.sortKey}`}
                    >
                      <div className="flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-[#FF4500]" />
                        <span className="text-sm font-medium text-white">{group.label}</span>
                        <span className="text-xs text-white/40 bg-white/8 px-1.5 py-0.5 rounded">{group.invoices.length}</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${openMonths[group.key] ? '' : '-rotate-90'}`} />
                    </button>
                    {openMonths[group.key] && (
                      <div className="divide-y divide-white/5">
                        {group.invoices.map((invoice) => (
                          <div 
                            key={invoice.id}
                            className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0">
                                {getStatusIcon(invoice.status)}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">{invoice.client_name}</p>
                                <p className="text-xs text-white/40">#{invoice.invoice_number}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right mr-1">
                                <p className="text-sm font-semibold text-white">
                                  {formatCurrency(invoice.balance_due, invoice.currency)}
                                </p>
                                <Select 
                                  value={invoice.status || 'draft'} 
                                  onValueChange={(value) => handleStatusChange(invoice.id, value)}
                                >
                                  <SelectTrigger className={`h-5 px-1.5 text-[10px] border-0 ${getStatusColor(invoice.status)}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#121216] border-white/10">
                                    <SelectItem value="draft" className="text-white hover:bg-white/5">Draft</SelectItem>
                                    <SelectItem value="sent" className="text-white hover:bg-white/5">Sent</SelectItem>
                                    <SelectItem value="paid" className="text-white hover:bg-white/5">Paid</SelectItem>
                                    <SelectItem value="overdue" className="text-white hover:bg-white/5">Overdue</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button variant="ghost" size="icon" onClick={() => setPreviewInvoice(invoice)} className="h-7 w-7 text-white/40 hover:text-white" title="Preview" data-testid={`preview-invoice-${invoice.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDuplicateInvoice(invoice)} className="h-7 w-7 text-white/40 hover:text-emerald-400" title="Duplicate" data-testid={`duplicate-invoice-${invoice.id}`}>
                                <Copy className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDownloadInvoicePDF(invoice)} className="h-7 w-7 text-white/40 hover:text-[#FF4500]" title="Download PDF" data-testid={`download-invoice-${invoice.id}`}>
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleSendInvoice(invoice)} className="h-7 w-7 text-white/40 hover:text-blue-400" title="Send Invoice" data-testid={`send-invoice-${invoice.id}`}>
                                <Send className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteInvoice(invoice.id)} className="h-7 w-7 text-white/40 hover:text-red-400" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Emails History */}
          <div className="bg-surface p-6 rounded-md border border-white/8 card-glow">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-clash text-xl font-medium text-white">Recent Emails</h2>
              <Link to="/email-generator">
                <Button size="sm" className="bg-[#FF4500] hover:bg-[#CC3700]">
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Button>
              </Link>
            </div>

            {letters.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50 mb-4">No emails generated yet</p>
                <Link to="/email-generator">
                  <Button variant="outline" className="border-white/10 text-white hover:bg-white/5">
                    Generate your first email
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {letters.slice(0, 5).map((letter) => (
                  <div 
                    key={letter.id}
                    className="flex items-center justify-between p-4 rounded-md bg-black/30 border border-white/5 hover:border-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-md bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#FF4500]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{letter.request_data?.client_name}</p>
                        <div className="flex items-center gap-3 text-sm text-white/50">
                          <span>{formatCurrency(letter.request_data?.amount_owed, letter.request_data?.currency)}</span>
                          <span>{formatDate(letter.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setViewingLetter(letter)}
                        className="text-white/40 hover:text-white"
                        title="View"
                        data-testid={`view-letter-${letter.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownloadLetterPDF(letter, 'friendly')}
                        className="text-white/40 hover:text-[#FF4500]"
                        title="Download PDF"
                        data-testid={`download-letter-${letter.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteLetter(letter.id)}
                        className="text-white/40 hover:text-red-400"
                        title="Delete"
                        data-testid={`delete-letter-${letter.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {letters.length > 5 && (
                  <button className="w-full py-3 text-center text-white/50 hover:text-white/70 text-sm flex items-center justify-center gap-1">
                    View all {letters.length} emails
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Calendar & Events Section - At Bottom */}
        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-surface p-6 rounded-md border border-white/8 card-glow">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-clash text-xl font-medium text-white flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#FF4500]" />
                Calendar & Events
              </h2>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                  onClick={handleExportAllEvents}
                  data-testid="export-calendar-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
                <Button 
                  size="sm" 
                  className="bg-[#FF4500] hover:bg-[#CC3700]"
                  onClick={() => handleOpenEventDialog(selectedDate)}
                  data-testid="add-event-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Event
                </Button>
              </div>
            </div>
            
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md"
                components={{
                  DayContent: ({ date }) => (
                    <div className="relative w-full h-full flex items-center justify-center">
                      {date.getDate()}
                      {getDayContent(date)}
                    </div>
                  )
                }}
              />
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-white/10">
              {EVENT_TYPES.map((type) => (
                <div key={type.value} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: type.color }}
                  />
                  <span className="text-sm text-white/60">{type.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Date Events */}
          <div className="bg-surface p-6 rounded-md border border-white/8 card-glow">
            <h3 className="font-medium text-white mb-4">
              {selectedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </h3>
            
            {selectedDateEvents.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50 text-sm mb-4">No events scheduled</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-white/10 text-white hover:bg-white/5"
                  onClick={() => handleOpenEventDialog(selectedDate)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Event
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map((event) => {
                  const eventType = EVENT_TYPES.find(t => t.value === event.event_type);
                  const Icon = eventType?.icon || CalendarIcon;
                  
                  return (
                    <div 
                      key={event.id}
                      className="p-3 rounded-md bg-black/30 border-l-4"
                      style={{ borderColor: event.color || '#FF4500' }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" style={{ color: event.color }} />
                          <span className="font-medium text-white text-sm">{event.title}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-6 w-6 text-white/40 hover:text-blue-400"
                            onClick={() => handleExportToGoogleCalendar(event.id)}
                            title="Add to Google Calendar"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-6 w-6 text-white/40 hover:text-purple-400"
                            onClick={() => handleSendReminder(event.id)}
                            title="Send Reminder Email"
                          >
                            <Mail className="w-3 h-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-6 w-6 text-white/40 hover:text-white"
                            onClick={() => handleOpenEventDialog(null, event)}
                            title="Edit Event"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-6 w-6 text-white/40 hover:text-red-400"
                            onClick={() => handleDeleteEvent(event.id)}
                            title="Delete Event"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      {event.time && (
                        <p className="text-xs text-white/50 mt-1">{event.time}</p>
                      )}
                      {event.client_name && (
                        <p className="text-xs text-white/60 mt-1">Client: {event.client_name}</p>
                      )}
                      {event.amount && (
                        <p className="text-xs text-[#FF4500] mt-1">{formatCurrency(event.amount, event.currency)}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-clash text-xl">
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Event Title *</Label>
              <Input
                placeholder="DJ Performance at Venue"
                value={eventForm.title}
                onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                data-testid="event-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Type</Label>
                <Select 
                  value={eventForm.event_type} 
                  onValueChange={(v) => setEventForm(prev => ({ ...prev, event_type: v }))}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121216] border-white/10">
                    {EVENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/5">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Date *</Label>
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm(prev => ({ ...prev, date: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Time</Label>
                <Input
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm(prev => ({ ...prev, time: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Client Name</Label>
                <Input
                  placeholder="Client name"
                  value={eventForm.client_name}
                  onChange={(e) => setEventForm(prev => ({ ...prev, client_name: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Amount</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={eventForm.amount}
                  onChange={(e) => setEventForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Currency</Label>
                <Select 
                  value={eventForm.currency} 
                  onValueChange={(v) => setEventForm(prev => ({ ...prev, currency: v }))}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121216] border-white/10 max-h-60 overflow-y-auto">
                    <SelectItem value="GBP" className="text-white hover:bg-white/5">GBP (£)</SelectItem>
                    <SelectItem value="USD" className="text-white hover:bg-white/5">USD ($)</SelectItem>
                    <SelectItem value="EUR" className="text-white hover:bg-white/5">EUR (€)</SelectItem>
                    <SelectItem value="AED" className="text-white hover:bg-white/5">AED (د.إ)</SelectItem>
                    <SelectItem value="CAD" className="text-white hover:bg-white/5">CAD ($)</SelectItem>
                    <SelectItem value="AUD" className="text-white hover:bg-white/5">AUD ($)</SelectItem>
                    <SelectItem value="JPY" className="text-white hover:bg-white/5">JPY (¥)</SelectItem>
                    <SelectItem value="INR" className="text-white hover:bg-white/5">INR (₹)</SelectItem>
                    <SelectItem value="CHF" className="text-white hover:bg-white/5">CHF (Fr)</SelectItem>
                    <SelectItem value="ZAR" className="text-white hover:bg-white/5">ZAR (R)</SelectItem>
                    <SelectItem value="NGN" className="text-white hover:bg-white/5">NGN (₦)</SelectItem>
                    <SelectItem value="SAR" className="text-white hover:bg-white/5">SAR (﷼)</SelectItem>
                    <SelectItem value="QAR" className="text-white hover:bg-white/5">QAR (﷼)</SelectItem>
                    <SelectItem value="KWD" className="text-white hover:bg-white/5">KWD (د.ك)</SelectItem>
                    <SelectItem value="BRL" className="text-white hover:bg-white/5">BRL (R$)</SelectItem>
                    <SelectItem value="SGD" className="text-white hover:bg-white/5">SGD ($)</SelectItem>
                    <SelectItem value="NZD" className="text-white hover:bg-white/5">NZD ($)</SelectItem>
                    <SelectItem value="SEK" className="text-white hover:bg-white/5">SEK (kr)</SelectItem>
                    <SelectItem value="NOK" className="text-white hover:bg-white/5">NOK (kr)</SelectItem>
                    <SelectItem value="MXN" className="text-white hover:bg-white/5">MXN ($)</SelectItem>
                    <SelectItem value="KES" className="text-white hover:bg-white/5">KES (KSh)</SelectItem>
                    <SelectItem value="JMD" className="text-white hover:bg-white/5">JMD ($)</SelectItem>
                    <SelectItem value="TTD" className="text-white hover:bg-white/5">TTD ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Notes</Label>
              <Input
                placeholder="Additional details..."
                value={eventForm.notes}
                onChange={(e) => setEventForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowEventDialog(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEvent}
              className="bg-[#FF4500] hover:bg-[#CC3700] text-white"
              data-testid="save-event-btn"
            >
              {editingEvent ? 'Update Event' : 'Add Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
      />

      {/* First-time walkthrough */}
      {showWalkthrough && (
        <WalkthroughDemo onComplete={() => setShowWalkthrough(false)} />
      )}

      {/* Invoice Preview Dialog */}
      <Dialog open={!!previewInvoice} onOpenChange={() => setPreviewInvoice(null)}>
        <DialogContent className="max-w-lg bg-[#0a0a0f] border border-white/10 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Invoice Preview</DialogTitle>
          </DialogHeader>
          {previewInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-white/40 text-xs">Invoice #</p>
                  <p className="text-white font-medium">{previewInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Status</p>
                  <p className={`font-medium capitalize ${getStatusColor(previewInvoice.status)}`}>{previewInvoice.status || 'draft'}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Client</p>
                  <p className="text-white font-medium">{previewInvoice.client_name}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Business</p>
                  <p className="text-white font-medium">{previewInvoice.business_name}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Date</p>
                  <p className="text-white">{previewInvoice.invoice_date || formatDate(previewInvoice.created_at)}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Due Date</p>
                  <p className="text-white">{previewInvoice.due_date || 'N/A'}</p>
                </div>
              </div>

              {previewInvoice.items?.length > 0 && (
                <div>
                  <p className="text-white/40 text-xs mb-2">Line Items</p>
                  <div className="bg-black/30 rounded-md border border-white/5 overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/5 text-xs text-white/40">
                      <span className="col-span-6">Description</span>
                      <span className="col-span-2 text-right">Qty</span>
                      <span className="col-span-2 text-right">Rate</span>
                      <span className="col-span-2 text-right">Amount</span>
                    </div>
                    {previewInvoice.items.map((item, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/5 last:border-0 text-sm">
                        <span className="col-span-6 text-white/70 truncate">{item.description}</span>
                        <span className="col-span-2 text-right text-white/60">{item.quantity}</span>
                        <span className="col-span-2 text-right text-white/60">{formatCurrency(item.rate, previewInvoice.currency)}</span>
                        <span className="col-span-2 text-right text-white font-medium">{formatCurrency(item.quantity * item.rate, previewInvoice.currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t border-white/8">
                <span className="text-white/50">Balance Due</span>
                <span className="text-xl font-bold text-white">{formatCurrency(previewInvoice.balance_due, previewInvoice.currency)}</span>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleDownloadInvoicePDF(previewInvoice)}
                  className="flex-1 bg-[#FF4500] hover:bg-[#e03e00] text-white"
                  data-testid="preview-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { handleDuplicateInvoice(previewInvoice); setPreviewInvoice(null); }}
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid="preview-duplicate-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendInvoice(previewInvoice)}
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                  data-testid="preview-send-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPreviewInvoice(null)}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email View Dialog */}
      <Dialog open={!!viewingLetter} onOpenChange={() => setViewingLetter(null)}>
        <DialogContent className="max-w-lg bg-[#0a0a0f] border border-white/10 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Email Details</DialogTitle>
          </DialogHeader>
          {viewingLetter && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-white/40 text-xs">Client</p>
                  <p className="text-white font-medium">{viewingLetter.request_data?.client_name}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Amount Owed</p>
                  <p className="text-white font-medium">{formatCurrency(viewingLetter.request_data?.amount_owed, viewingLetter.request_data?.currency)}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Business</p>
                  <p className="text-white">{viewingLetter.request_data?.business_name}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs">Generated</p>
                  <p className="text-white">{formatDate(viewingLetter.created_at)}</p>
                </div>
              </div>

              {/* Email tones tabs */}
              <div className="space-y-2">
                {['friendly', 'firm', 'legal'].map((tone) => {
                  const content = viewingLetter.letters?.[tone];
                  if (!content) return null;
                  return (
                    <details key={tone} className="bg-black/30 rounded-md border border-white/5 overflow-hidden group" data-testid={`letter-tone-${tone}`}>
                      <summary className="px-4 py-3 cursor-pointer flex justify-between items-center text-sm font-medium text-white/80 hover:text-white">
                        <span className="capitalize">{tone} Tone</span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-white/40 hover:text-[#FF4500]"
                            onClick={(e) => { e.preventDefault(); handleDownloadLetterPDF(viewingLetter, tone); }}
                            title={`Download ${tone} PDF`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-white/30 group-open:rotate-90 transition-transform" />
                        </div>
                      </summary>
                      <div className="px-4 pb-4">
                        <pre className="whitespace-pre-wrap text-white/60 text-xs font-sans leading-relaxed max-h-48 overflow-y-auto">
                          {content}
                        </pre>
                      </div>
                    </details>
                  );
                })}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleDownloadLetterPDF(viewingLetter, 'friendly')}
                  className="flex-1 bg-[#FF4500] hover:bg-[#e03e00] text-white"
                  data-testid="letter-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    handleDeleteLetter(viewingLetter.id);
                    setViewingLetter(null);
                  }}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="letter-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setViewingLetter(null)}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
